<script setup lang="ts">
/**
 * VideoPlaceholder Component
 *
 * This component conditionally displays a placeholder image or a video based on the `isPlaying` prop.
 * It ensures smooth transitions between the two and handles video autoplay behavior.
 *
 * ## Props:
 * - `videoSrc` (string, required): The source URL of the video.
 * - `placeholder` (string, required): The source URL of the placeholder image.
 * - `alt` (string, optional): Alternative text for the placeholder image.
 * - `classPlaceholder` (string, optional): CSS classes for styling the placeholder image.
 * - `classVideo` (string, optional): CSS classes for styling the video.
 * - `style` (string | Record<string, string>, optional): Inline styles for both the video and image.
 * - `isPlaying` (boolean, required): Controls whether the video is playing or the placeholder is displayed.
 *
 * ## Behavior:
 * - If `isPlaying` is `true`, the component attempts to play the video and hides the placeholder.
 * - If `isPlaying` is `false`, the component pauses the video and shows the placeholder.
 * - Watches for changes in `videoSrc`, reloading the video when it changes.
 * - Handles autoplay failures by catching errors.
 *
 * ## Usage Example:
 * <VideoPlaceholder
 *   videoSrc="/path/to/video.mp4"
 *   placeholder="/path/to/placeholder.jpg"
 *   alt="Video thumbnail"
 *   classPlaceholder="w-full h-auto"
 *   classVideo="w-full h-auto"
 *   :isPlaying="true"
 * />
 */

import { ref, watch, watchEffect } from "vue";
import { UserAnimationEnabled } from "~/states/animation";
const { animationEnabled } = useCustom();
// Props for customization
const {
  videoSrc,
  placeholder,
  alt,
  classPlaceholder,
  classVideo,
  style,
  isPlaying,
} = defineProps({
  isPlaying: Boolean,
  videoSrc: String,
  placeholder: String,
  alt: String,
  classPlaceholder: String,
  classVideo: String,
  style: { type: [String, Object], required: false },
});
const animation = UserAnimationEnabled();
const videoElement = ref<HTMLVideoElement | null>(null);
const isVideoReady = ref<boolean>(false);

watchEffect(() => {
  if (videoElement.value) {
    if (isPlaying && animation.value) {
      videoElement.value.play().then(() => {
        isVideoReady.value = true;
      });
    }
  }
});
watch(
  () => videoSrc,
  (newSrc) => {
    if (videoElement.value) {
      videoElement.value.pause();
      videoElement.value.src = newSrc ?? "";
      videoElement.value.load();
      videoElement.value.play();
    }
  }
);
onMounted(() => {
  animation.value = animationEnabled;
});
</script>

<template>
  <img
    v-show="!isVideoReady || !isPlaying"
    :style="style"
    :src="placeholder"
    :alt="alt || $t('alt.placeholder')"
    :class="classPlaceholder"
    fetchpriority="high"
  />
  <video
    v-show="isVideoReady && isPlaying"
    ref="videoElement"
    playsinline
    preload="none"
    loop
    :style="style"
    muted
    :autoplay="isPlaying"
    :class="classVideo"
  >
    <source :src="videoSrc" type="video/mp4" />
    Your browser does not support the video tag.
  </video>
</template>
